// Phase 1/2 PWA cache policy (pure functions).
// Service Worker 本体からも同じ判定を使う。

export const SHELL_CACHE_PREFIX = "firmee-shell-v"
export const PRIVATE_CACHE_PREFIX = "firmee-private-"
export const OFFLINE_LANDING_URL = "/matters?offline_snapshot=1"
export const OFFLINE_CACHEABLE_HEADER = "X-Firmee-Offline-Cacheable"
export const OFFLINE_SUBJECT_HEADER = "X-Firmee-Offline-Subject"

const DENIED_PATH_PREFIXES = [
  "/login",
  "/signup",
  "/sign_in",
  "/sign_out",
  "/users/sign_in",
  "/users/sign_out",
  "/users/auth",
  "/users/magic_link_login",
  "/auth/",
  "/oauth",
  "/admin",
  "/sidekiq",
  "/payments",
  "/payment_gateways",
  "/rails/active_storage",
  "/rails/blobs",
  "/offline/catalog"
]

const DENIED_PATH_SUFFIXES = [".csv"]

const DENIED_PATH_INCLUDES = [
  "/documents",
  "/upload",
  "/attachments"
]

const OFFLINE_QUERY_ALLOWLIST = new Set(["offline_snapshot", "matter_id", "type"])
const OPAQUE_TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/

export function shellCacheName(assetVersion) {
  return `${SHELL_CACHE_PREFIX}${assetVersion}`
}

export function privateCacheName(partition, generation) {
  return `${PRIVATE_CACHE_PREFIX}${partition}-${generation}`
}

export function isObsoleteShellCache(cacheName, currentAssetVersion) {
  return (
    typeof cacheName === "string" &&
    cacheName.startsWith(SHELL_CACHE_PREFIX) &&
    cacheName !== shellCacheName(currentAssetVersion)
  )
}

export function isObsoletePrivateCache(cacheName, keepName) {
  if (typeof cacheName !== "string" || !cacheName.startsWith(PRIVATE_CACHE_PREFIX)) return false
  if (!keepName) return true
  return cacheName !== keepName
}

export function isNonCacheableMethod(method) {
  const normalized = String(method || "GET").toUpperCase()
  return normalized !== "GET"
}

export function isDeniedPath(pathname) {
  const path = String(pathname || "")
  if (DENIED_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`) || (prefix.endsWith("/") && path.startsWith(prefix)))) {
    return true
  }
  if (DENIED_PATH_SUFFIXES.some((suffix) => path.endsWith(suffix))) {
    return true
  }
  if (DENIED_PATH_INCLUDES.some((fragment) => path.includes(fragment))) {
    return true
  }
  return false
}

export function isFingerprintedAssetPath(pathname) {
  return /^\/assets\/.+-[a-zA-Z0-9]{8,}\.[A-Za-z0-9.:]+$/.test(String(pathname || ""))
}

export function isSafeSameOriginImagePath(pathname) {
  const path = String(pathname || "")
  if (isDeniedPath(path)) return false
  if (path.startsWith("/rails/")) return false
  return (
    path === "/icon.png" ||
    path === "/icon-maskable.png" ||
    path === "/favicon.ico" ||
    path.startsWith("/apple-touch-icon") ||
    path.startsWith("/images/")
  )
}

export function normalizeOfflineUrl(url, origin = "http://localhost") {
  const parsed = new URL(url, origin)
  const params = new URLSearchParams()
  const entries = []
  parsed.searchParams.forEach((value, key) => {
    if (OFFLINE_QUERY_ALLOWLIST.has(key)) entries.push([key, value])
  })
  entries.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]))
  for (const [key, value] of entries) params.append(key, value)
  const query = params.toString()
  return `${parsed.pathname}${query ? `?${query}` : ""}`
}

// Service Worker の private snapshot 照合。urls が配列かつ正規化 URL が明示的に含まれる場合だけ key を返す。
export function privateSnapshotMatchKey(snapshot, requestUrl, { origin = "http://localhost", now = Date.now() } = {}) {
  if (!snapshot) return null
  if (typeof snapshot.partition !== "string" || !OPAQUE_TOKEN_PATTERN.test(snapshot.partition)) return null
  if (typeof snapshot.generation !== "string" || !OPAQUE_TOKEN_PATTERN.test(snapshot.generation)) return null
  if (typeof snapshot.cacheName !== "string") return null
  if (snapshot.cacheName !== privateCacheName(snapshot.partition, snapshot.generation)) return null

  const parsedExpiry = Date.parse(snapshot.expiresAt)
  if (!Number.isFinite(parsedExpiry) || parsedExpiry <= now) return null
  if (!Array.isArray(snapshot.urls)) return null

  const key = normalizeOfflineUrl(requestUrl, origin)
  if (!snapshot.urls.includes(key)) return null
  return key
}

export function offlineLandingMatchKey(snapshot, options = {}) {
  return privateSnapshotMatchKey(snapshot, OFFLINE_LANDING_URL, options)
}

function headerValue(headers, name) {
  if (!headers) return null
  if (typeof headers.get === "function") return headers.get(name)
  const key = Object.keys(headers).find((candidate) => candidate.toLowerCase() === name.toLowerCase())
  return key ? headers[key] : null
}

export function canStoreNavigationResponse(responseLike, { expectedPartition } = {}) {
  if (!responseLike || responseLike.status !== 200 || responseLike.redirected) return false

  const cacheable = headerValue(responseLike.headers, OFFLINE_CACHEABLE_HEADER)
  if (cacheable !== "1") return false

  if (expectedPartition) {
    const subject = headerValue(responseLike.headers, OFFLINE_SUBJECT_HEADER)
    if (subject !== expectedPartition) return false
  }

  const contentType = String(headerValue(responseLike.headers, "content-type") || "").toLowerCase()
  if (contentType.includes("text/vnd.turbo-stream.html")) return false
  if (contentType && !contentType.includes("text/html")) return false

  const vary = String(headerValue(responseLike.headers, "vary") || "")
  if (vary.split(",").map((part) => part.trim()).includes("*")) return false

  const disposition = String(headerValue(responseLike.headers, "content-disposition") || "").toLowerCase()
  if (disposition.includes("attachment")) return false

  return true
}

export function isDocumentHtmlGet({ method, headers } = {}) {
  if (isNonCacheableMethod(method || "GET")) return false
  if (!headers) return false
  // Turbo-Frame / Turbo Stream はページ全体のオフラインフォールバック対象外
  if (headerValue(headers, "Turbo-Frame")) return false
  const accept = String(headerValue(headers, "Accept") || "").toLowerCase()
  if (accept.includes("text/vnd.turbo-stream.html")) return false
  return accept.includes("text/html")
}

export function cacheStrategyFor({ method, pathname, mode, destination, headers } = {}) {
  if (isNonCacheableMethod(method)) return "network-only"
  if (isDeniedPath(pathname)) return "network-only"
  if (isFingerprintedAssetPath(pathname)) return "cache-first"
  if (destination === "image" || isSafeSameOriginImagePath(pathname)) {
    if (isSafeSameOriginImagePath(pathname)) return "stale-while-revalidate"
  }
  // Turbo Drive の GET は mode=navigate ではないため Accept:text/html も network-first とする
  if (mode === "navigate" || isDocumentHtmlGet({ method, headers })) return "network-first"
  if (pathname === "/manifest.webmanifest") return "stale-while-revalidate"
  return "network-only"
}
